import React, { useState } from "react";
import { useDispatch } from "react-redux";
import InfoCodes from "./InfoCodes";
import CampaignCodes from "./CampaignCodes";
import FinanceCodes from "./FinanceCodes";
import OtherAddresses from "./OtherAddresses";
import MaterialModal from "../MaterialModal";
import withModal from "../withModal";
import { hideModal } from "../../../actions/actionCreators";
import {
    saveInfoCodesAsync,
    saveCampaignCodesAsync,
    saveFinanceCodesAsync,
    updateOtherAddress,
    deleteOtherAddress,
    addOtherAddress,
    updateDonationData,
    GetDonationData,
} from "../../../actions/makeAWish";
import { Address, DonationData } from "../../../types/stores/makeAWish";
import PromotionDataForm from "./PromotionDataForm";
import DonationDataForm from "./DonationDataForm";
import LinkedDonationData from "./LinkedDonationDataForm";

type ModalProps = {
    type: "info" | "finance" | "campaign" | "promotion" | "donation" | "other" | "donationlink";
    id: string | undefined; // Donor Id
    promoId: string | undefined; // PromotionId
    batchNumber: string | undefined;
    sequenceNumber: string | undefined;
};

type Props = {
    isOpen: boolean;
    modalProps: ModalProps;
};

type OtherType = "" | "add" | "edit" | "delete";

const EditDonorModal: React.FC<Props> = ({ isOpen, modalProps }: Props) => {
    const dispatch = useDispatch();

    const [codes, setCodes] = useState<Array<string>>([]);
    const [address, setAddress] = useState<Address | undefined>(undefined);
    const [donation, setDonation] = useState<DonationData | undefined>(undefined);
    const [canSave, setCanSave] = useState(false);
    const [otherAddressSaveType, setOtherAddressSaveType] = useState<OtherType>("");

    const hide = () => {
        dispatch(hideModal("EDIT_DONORS"));
        setCodes([]);
        setCanSave(false);
        dispatch(GetDonationData({}));
    };

    const save = () => {
        if (canSave) {
            switch (modalProps.type) {
                case "info":
                    dispatch(saveInfoCodesAsync(modalProps.id || "", codes));
                    break;
                case "campaign":
                    dispatch(saveCampaignCodesAsync(modalProps.id || "", codes));
                    break;
                case "finance":
                    dispatch(saveFinanceCodesAsync(modalProps.id || "", codes));
                    break;
                case "donation":
                    dispatch(updateDonationData(modalProps.id || "", donation || {}));
                    break;
                case "other":
                    if (address) {
                        if (otherAddressSaveType == "add") {
                            dispatch(addOtherAddress(modalProps.id || "", address));
                        } else if (otherAddressSaveType == "edit") {
                            dispatch(updateOtherAddress(modalProps.id || "", address));
                        } else if (otherAddressSaveType == "delete") {
                            dispatch(deleteOtherAddress(modalProps.id || "", address));
                        }
                    }
                    setOtherAddressSaveType("");
                    break;
                default:
                    break;
            }
        }

        if (modalProps.type != "other") {
            hide();
        } else {
            setCanSave(false);
        }
    };

    const onChange = (newCodes, hasErrors = false) => {
        setCodes(newCodes);
        setCanSave(!hasErrors);
    };

    const onOtherChange = (address, saveAllowed = false, type: OtherType = "") => {
        setAddress(address);
        setCanSave(saveAllowed);
        setOtherAddressSaveType(type);
    };

    const onDonationChange = (donation, saveAllowed = false) => {
        setDonation(donation);
        setCanSave(saveAllowed);
    };

    const getTitle = () => {
        let title;

        switch (modalProps.type) {
            case "info":
                title = "Info Codes";
                break;
            case "campaign":
                title = "Campaign Codes";
                break;
            case "finance":
                title = "Finance Codes";
                break;
            case "promotion":
                title = "Promotion Summary";
                break;
            case "donationlink":
                title = "Donation Link Summary";
                break;
            case "donation":
                title = "Donation Summary";
                break;
            case "other":
                title = otherAddressSaveType + " Other Addresses";
                break;
            default:
                title = "";
        }

        return title;
    };

    const getBody = () => {
        let body;
        switch (modalProps.type) {
            case "info":
                body = <InfoCodes id={modalProps.id || ""} onChange={onChange} />;
                break;
            case "campaign":
                body = <CampaignCodes id={modalProps.id || ""} onChange={onChange} />;
                break;
            case "finance":
                body = <FinanceCodes id={modalProps.id || ""} onChange={onChange} />;
                break;
            case "promotion":
                body = <PromotionDataForm id={modalProps.id || ""} promoId={modalProps.promoId || ""} />;
                break;
            case "donationlink":
                body = (
                    <LinkedDonationData
                        id={modalProps.id || ""}
                        batchNumber={modalProps.batchNumber || ""}
                        sequenceNumber={modalProps.sequenceNumber || ""}
                    />
                );
                break;
            case "donation":
                body = (
                    <DonationDataForm
                        id={modalProps.id || ""}
                        batchNumber={modalProps.batchNumber || ""}
                        sequenceNumber={modalProps.sequenceNumber || ""}
                        onChange={onDonationChange}
                        loadedData={isOpen}
                    />
                );
                break;
            case "other":
                body = <OtherAddresses id={modalProps.id || ""} type={otherAddressSaveType} onChange={onOtherChange} />;
                break;
            default:
                body = <div />;
        }

        return body;
    };

    return (
        <MaterialModal
            id={"edit-donor-modal" + modalProps.type}
            isOpen={isOpen}
            onClose={() => hide()}
            title={getTitle()}
            maxWidth="xl"
            fullWidth={modalProps.type == "other"}
            onPrimaryButtonRequest={() => save()}
            isPrimaryButtonDisabled={!canSave}
            showPrimaryButton
            primaryButtonText="Save"
            cancelButtonText="Cancel"
        >
            {getBody()}
        </MaterialModal>
    );
};

export default withModal(EditDonorModal, "EDIT_DONORS");
